import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/contexts/AuthContext"
import {
  isRegistrationEnabled,
  REGISTRATION_DISABLED_MESSAGE,
} from "@/lib/features"

function RegisterForm() {
  const [displayName, setDisplayName] = useState("")
  const [organizationName, setOrganizationName] = useState("")
  const [email, setEmail] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sentMessage, setSentMessage] = useState<string | null>(null)
  const { register: doRegister } = useAuth()
  const navigate = useNavigate()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const result = await doRegister({
      email,
      displayName,
      organizationName: organizationName.trim() || undefined,
    })
    setSubmitting(false)
    if (result.ok) {
      setSentMessage(result.message)
      return
    }
    setError(result.error)
  }

  if (sentMessage) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-sm text-foreground" role="status">
          {sentMessage}
        </p>
        <p className="text-sm text-muted-foreground">
          Open the link in that email to set your password and finish creating
          your account.
        </p>
        <Button asChild className="w-full">
          <Link to="/login">Back to sign in</Link>
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <p className="text-center text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      <div className="space-y-2">
        <label htmlFor="register-name" className="text-sm font-medium">
          Name
        </label>
        <Input
          id="register-name"
          type="text"
          autoComplete="name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your name"
          required
          className="w-full"
        />
      </div>
      <div className="space-y-2">
        <label htmlFor="register-org" className="text-sm font-medium">
          Organisation name (optional)
        </label>
        <Input
          id="register-org"
          type="text"
          value={organizationName}
          onChange={(e) => setOrganizationName(e.target.value)}
          placeholder="Your company or team"
          className="w-full"
        />
      </div>
      <div className="space-y-2">
        <label htmlFor="register-email" className="text-sm font-medium">
          Email
        </label>
        <Input
          id="register-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          className="w-full"
        />
      </div>
      <p className="text-sm text-muted-foreground">
        We'll email you a link to verify your address and set a password.
      </p>
      <div className="flex gap-2">
        <Button type="submit" className="flex-1" disabled={submitting}>
          {submitting ? "Sending…" : "Send verification link"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/login")}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}

export function Register() {
  const registrationEnabled = isRegistrationEnabled()

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-4 rounded-lg border p-6 shadow-sm">
        <h1 className="text-center text-xl font-semibold">Register</h1>
        {!registrationEnabled && (
          <p className="text-center text-sm text-muted-foreground" role="status">
            {REGISTRATION_DISABLED_MESSAGE}
          </p>
        )}
        {registrationEnabled ? (
          <RegisterForm />
        ) : (
          <Button asChild className="w-full">
            <Link to="/login">Back to sign in</Link>
          </Button>
        )}
      </div>
    </div>
  )
}
